/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { listTiming, updateTiming } from "../core/_requests";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../../auth";
import CloseButton from "../../../component/core/Buttons";
import ListSettings from "./ListSettings";
import { useWorkingHours } from "../../../component/context/WorkingHoursContext";
import { normalizeWorkingHours } from "../../../timingConfig";
import { settingsDetails } from "../../../auth/core/_requests";
import { formatToAMPM } from "../../../component/dateUtils";
import { Modal } from "react-bootstrap";

interface Addtime {
  id: string;
  chefId: string;
  isClosed: boolean;
  isAuto: boolean;
  timingType: boolean;
  openingTime: string;
  closingTime: string;
  status: string;
  day: string;
}

type Props = {
  initialValues: any;
};

const getTimeInMinutes = (time?: string) => {
  if (!time || !time.includes(":")) return null;
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

const ChangeChefTiming: FC<Props> = ({ initialValues }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(initialValues);
  const chefId = currentUser?.uuid;
  const [isAuto, setIsAuto] = useState<boolean>(false);
  const [timingType, setTimingType] = useState<boolean>(false);
  const { workingHours, updateWorkingHours } = useWorkingHours();

  const [localHours, setLocalHours] = useState(workingHours);
  const [isClosed, setIsClosed] = useState<boolean[]>(
    initialValues.map((item: { isClosed: any }) => item.isClosed)
  );

  const inputs = useRef<Array<HTMLInputElement | HTMLSelectElement | null>>([]);

  const [settings, setSettings] = useState<any>({});
  const [showTimezoneModal, setShowTimezoneModal] = useState(false); // ⭐ ADD THIS

  useEffect(() => {
    listTiming(chefId).then((res) => {
      setIsAuto(res.data[0].isAuto);
      setTimingType(res.data[0].timingType);
      setData(res.data);
      const normalized = normalizeWorkingHours(res.data);
      updateWorkingHours(normalized);
    });
  }, [chefId]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await settingsDetails(1);
        setSettings(res.data);
      } catch (error) {
        console.error("Error fetching Settings details:", error);
      }
    };
    fetchSettings();
  }, []);

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTimingType = e.target.value === "true";
    setTimingType(newTimingType);
    formik.setFieldValue("timingType", newTimingType);
  };

  const clickCancel = () => {
    navigate("/settings");
  };

  const handleTimeChange = (day: string, field: string, value: string) => {
    const newData = [...data];
    const target =
      day === "all" ? newData[0] : newData.find((item) => item.day === day);
    if (!target) return;

    const openingTime = field === "openingTime" ? value : target.openingTime;
    const closingTime = field === "closingTime" ? value : target.closingTime;

    const [openH, openM] = openingTime.split(":").map(Number);
    const [closeH, closeM] = closingTime.split(":").map(Number);

    const openingMinutes = openH * 60 + openM;
    const closingMinutes = closeH * 60 + closeM;

    const [maxopenH, maxopenM] = settings?.opening_time.split(":").map(Number);
    const [maxcloseH, maxcloseM] = settings?.closing_time.split(":").map(Number);

    const minOpeningMinutes = maxopenH * 60 + maxopenM;
    const maxClosingMinutes = maxcloseH * 60 + maxcloseM;

    if (field === "openingTime" && openingMinutes !== null) {
      if (
        openingMinutes < minOpeningMinutes ||
        openingMinutes > maxClosingMinutes
      ) {
        toast.error(
          `Opening time must be between ${formatToAMPM(
            settings?.opening_time
          )} and ${formatToAMPM(settings?.closing_time)}`
        );
        return;
      }
      if (closingMinutes !== null && closingMinutes <= openingMinutes) {
        toast.error("Closing time must be after opening time");
        return;
      }
    }

    if (field === "closingTime" && closingMinutes !== null) {
      if (closingMinutes <= openingMinutes) {
        toast.error("Closing time must be after opening time");
        return;
      }
      if (closingMinutes > maxClosingMinutes) {
        toast.error(
          `Closing time cannot be later than ${formatToAMPM(
            settings?.closing_time
          )}`
        );
        return;
      }
      if (closingMinutes < minOpeningMinutes) {
        toast.error(
          `Closing time cannot be earlier than ${formatToAMPM(
            settings?.opening_time
          )}`
        );
        return;
      }
    }

    const updated = newData.map((item) => {
      if (day === "all" || item.day === day) {
        return { ...item, [field]: value };
      }
      return item;
    });

    setData(updated);
  };

  const handleCheckboxChange = (index: number) => {
    setIsClosed((prevState) => {
      const newState = [...prevState];
      newState[index] = !newState[index];
      return newState;
    });
  };

  const formik = useFormik<Addtime>({
    initialValues,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);

      const updatedData = data.map((item: any, index: number) => ({
        chefId,
        openingTime: item.openingTime,
        closingTime: item.closingTime,
        day: item.day,
        isClosed: isClosed[index],
      }));

      try {
        const res = await updateTiming({
          timingType: timingType,
          isAuto: isAuto,
          chefWorkingHours: updatedData,
        });

        if (res) {
          setLoading(false);
          toast.success(res?.message, { autoClose: 6000 });

          const workingHoursFormat = normalizeWorkingHours(updatedData);
          updateWorkingHours(workingHoursFormat);

          navigate("/settings");
        } else {
          setLoading(false);
        }
      }catch (error: any) {
  console.error("🔥 FULL ERROR:", error);

  const backendMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "";

  // 🚨 If timezone error → Show modal (NOT toast)
  if (
    backendMessage.includes("Invalid time zone") ||
    backendMessage.includes("timezone") ||
    backendMessage.includes("null")
  ) {
    setShowTimezoneModal(true);
    setLoading(false);
    return;
  }

  // ❌ All other errors → show toast
  toast.error("Oops! Something Went Wrong While Updating the Timing.");
  setLoading(false);
   setShowTimezoneModal(true);
}


      resetForm();
    },
  });

  return (
    <>
      <section className="container-fluid">
        <ListSettings />
        <div className="row">
          <section className="content-section">
            <div className="overlay"></div>
            <div className="drawer small">
              <div className="title row">
                <h3 className="col-10 mb-0">Business Timings</h3>
                <div className="col-2 text-end">
                  <button className="btn p-0 close d-inline-flex" onClick={clickCancel}>
                    <i className="material-symbols-outlined">close</i>
                  </button>
                </div>
              </div>

              <form
                onSubmit={formik.handleSubmit}
                noValidate
                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                className="row mt-4 add-bank-form"
              >
                <div className="col-12 mb-3">
                  <label className="form-label">Working Days & Timings</label>
                  <div className="radio-btn-row">
                    <div className="form-check">
                      <label className="form-check-label">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="timingType"
                          value="true"
                          checked={timingType}
                          onChange={handleRadioChange}
                        />
                        All Days
                      </label>
                    </div>

                    <div className="form-check">
                      <label className="form-check-label">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="timingType"
                          value="false"
                          checked={!timingType}
                          onChange={handleRadioChange}
                        />
                        Specific Days & Timings
                      </label>
                    </div>
                  </div>
                </div>

                <div className="alert alert-warning py-2 px-3 my-2">
                  <small>
                    ⚠️Only times between {formatToAMPM(settings?.opening_time)} and{" "}
                    {formatToAMPM(settings?.closing_time)} are allowed.
                  </small>
                </div>

                {timingType ? (
                  <div className="col-12 mb-3">
                    <div className="d-flex">
                      <label className="form-label w-50">Start Time</label>
                      <label className="form-label w-50">End Time</label>
                    </div>
                    <div className="input-group">
                      <input
                        type="time"
                        className="form-control"
                        value={data[0].openingTime}
                        onChange={(e) =>
                          handleTimeChange("all", "openingTime", e.target.value)
                        }
                      />

                      <input
                        type="time"
                        className="form-control"
                        value={data[0].closingTime}
                        onChange={(e) =>
                          handleTimeChange("all", "closingTime", e.target.value)
                        }
                      />
                    </div>
                  </div>
                ) : (
                  data.map((item: any, i: number) => (
                    <div className="col-12 mt-3 mb-3" key={i}>
                      <div className="row align-items-center">
                        <div className="col-3">
                          <div className="form-check form-switch days">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={!isClosed[i]}
                              onChange={() => handleCheckboxChange(i)}
                            />
                            <label className="yes">{item.day}</label>
                            <label className="no">{item.day}</label>
                          </div>
                        </div>

                        <div className="col-9">
                          <div className="input-group">
                            <input
                              type="time"
                              className="form-control"
                              value={item.openingTime}
                              onChange={(e) =>
                                handleTimeChange(item.day, "openingTime", e.target.value)
                              }
                              disabled={isClosed[i]}
                            />

                            <input
                              type="time"
                              className="form-control"
                              value={item.closingTime}
                              onChange={(e) =>
                                handleTimeChange(item.day, "closingTime", e.target.value)
                              }
                              disabled={isClosed[i]}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}

                <div className="col-12 mt-4 mb-3">
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={isAuto}
                      onChange={() => setIsAuto(!isAuto)}
                    />
                    <label className="form-check-label ms-2">
                      <small>Enable Auto Online/Offline</small>
                    </label>
                  </div>
                </div>

                <div className="col-12 mt-3 text-end btn-bottom">
                  <button className="btn btn-cancel rounded-5">Cancel</button>
                  <button className="btn btn-primary rounded-5" type="submit">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </section>
        </div>

        {/* ⭐ TIMEZONE UPDATE MODAL */}
        <Modal show={showTimezoneModal} onHide={() => setShowTimezoneModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Update Address Required</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p>Your address does not have a valid timezone.</p>
            <p className="fw-bold text-danger">
              Please update your current address before enabling Auto Online/Offline.
            </p>
          </Modal.Body>

          <Modal.Footer>
            <button
              className="btn btn-secondary"
              onClick={() => setShowTimezoneModal(false)}
            >
              Cancel
            </button>

            <button
              className="btn btn-primary"
              onClick={() => navigate("/settings/profile-details")}
            >
              Update Address
            </button>
          </Modal.Footer>
        </Modal>
      </section>
    </>
  );
};

export { ChangeChefTiming };

